'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import React, { useEffect, useContext, useMemo, useState, Suspense } from 'react';
import { useLocalStorage, useSessionStorage } from 'usehooks-ts';
import { CampaignTrackerContext, TrackingInfo } from '../campaign-tracker-context';

type StoredInfo = {
  landing_page?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  querystring?: string;
};

export const useCampaignTracker = () => {
  const [trackingInfo] = useContext(CampaignTrackerContext) ?? [];


  return useMemo(() => trackingInfo ?? {}, [trackingInfo]);
};

export const CampaignTrackerProvider = (props: { children: React.ReactNode; }) => {
  const { children } = props;

  const stateHook = useState<TrackingInfo | undefined>();

  return (
    <CampaignTrackerContext.Provider value={stateHook}>
      {children}
    </CampaignTrackerContext.Provider>
  );
};

const CampaignTrackerSync = () => {
  const [, setTrackingInfo] = useContext(CampaignTrackerContext) ?? [];

  const path = usePathname();
  const searchParams = useSearchParams();

  const [landingPage] = useState(path || undefined);
  const [sessionState, setSessionState] = useSessionStorage<StoredInfo>('_ctp_', {});
  const [localState, setLocalState] = useLocalStorage<StoredInfo>('_ctp_', {});

  useEffect(() => {
    const query = searchParams ? Object.fromEntries(searchParams) : {};

    const {
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
      utm_term,
      ...other
    } = query;

    const queryValueToString = (value: string | string[] | undefined) => {
      if (Array.isArray(value)) { return value[0]; }
      return value;
    };

    const queryToString = (query: Record<string, string | string[] | undefined>) => {
      const count = Object.values(query).length;
      if (count === 0) { return ''; }

      return JSON.stringify(query);
    };

    const applyInfo = (s: StoredInfo) => ({
      landing_page: s.landing_page || landingPage,
      utm_source: s.utm_source || queryValueToString(utm_source),
      utm_medium: s.utm_medium || queryValueToString(utm_medium),
      utm_campaign: s.utm_campaign || queryValueToString(utm_campaign),
      utm_content: s.utm_content || queryValueToString(utm_content),
      utm_term: s.utm_term || queryValueToString(utm_term),
      querystring: s.querystring || queryToString(other),
    });

    setSessionState(applyInfo);
    setLocalState(applyInfo);
  }, [landingPage, searchParams, setLocalState, setSessionState]);

  const state = useMemo<TrackingInfo>(() => ({
    landing_page: sessionState.landing_page,
    utm_source: sessionState.utm_source,
    utm_medium: sessionState.utm_medium,
    utm_campaign: sessionState.utm_campaign,
    utm_content: sessionState.utm_content,
    utm_term: sessionState.utm_term,
    querystring: sessionState.querystring,

    first_landing_page: localState.landing_page,
    first_utm_source: localState.utm_source,
    first_utm_medium: localState.utm_medium,
    first_utm_campaign: localState.utm_campaign,
    first_utm_content: localState.utm_content,
    first_utm_term: localState.utm_term,
    first_querystring: localState.querystring,
  }), [localState, sessionState]);

  useEffect(() => {
    if (!setTrackingInfo) { return; }
    setTrackingInfo(state);
  }, [setTrackingInfo, state]);

  return null;
};

export const AsyncCampaignTrackerSync = () => (
  <Suspense>
    <CampaignTrackerSync />
  </Suspense>
);
